<template>
  <VCard class="overflow-x-auto">
    <div class="flex flex-col gap-4 bg-white rounded-md px-4 py-2 w-full xl:min-w-[750px]">
      <div class="flex flex-row flex-wrap justify-between">
        <div class="flex flex-row flex-wrap items-baseline gap-2 text-primary-text flex-1 py-2">
          <p
            class="text-primary font-semibold text-h5 text-truncate cursor-pointer"
            data-testid="patient-name"
            @click="redirectToPatientDetails"
          >
            {{ getFormattedPatientName(caze.patient) }}
          </p>
          <PatientDetails :caze="caze" class="text-body-2" previewMode />
        </div>

        <div class="flex flex-row flex-wrap items-center gap-2">
          <VBtn
            v-if="canCreateConsultations && isConnectEnabled"
            :active="false"
            :class="isConsultationDisabled ? 'opacity-50' : 'opacity-100'"
            color="primary"
            variant="text"
            :disabled="isConsultationDisabled"
            @click="onCreateConsultation"
          >
            <template #prepend>
              <img
                src="/calendar-add.svg"
                alt="Create consultation"
                class="place-self-center w-[20px] h-[20px]"
              />
            </template>

            {{ t('Create consultation') }}
          </VBtn>

          <VBtn
            v-if="canCreateConsultations && isConnectEnabled"
            :active="false"
            :class="caze.emergencyRoutingUnavailable ? 'opacity-50' : 'opacity-100'"
            :disabled="caze.emergencyRoutingUnavailable"
            color="secondary"
            variant="text"
            data-testid="emergency-button"
            @click="emit('emergency-click', caze.id)"
          >
            <template #prepend>
              <VIcon color="secondary" size="20" icon="emergency" class="material-icons-round" />
            </template>

            {{ t('Emergency Call') }}
          </VBtn>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div
          class="gap-2 justify-between grid"
          :class="{
            'grid-cols-3 md:grid-cols-5 xl:grid-cols-10': !isSidebarVisible,
            'grid-cols-2 md:grid-cols-4 xl:grid-cols-7': isSidebarVisible
          }"
        >
          <!-- Vital Signs -->
          <VTooltip
            v-for="(vital, index) in vitalSignsCards"
            :key="index"
            :text="vital.displayDate"
            :location="'bottom center'"
            :open-delay="500"
            :offset="-5"
          >
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="bg-[#F3F4F7] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
              >
                <p class="text-caption text-[#206683]">{{ vital.label }}</p>
                <p class="text-subtitle-2 font-bold text-[#17485D]">
                  {{ vital.value }}
                </p>
              </div>
            </template>
          </VTooltip>

          <VTooltip
            v-if="isEdsEnabled"
            :text="edsTooltipText"
            location="bottom center"
            :open-delay="500"
            :offset="-5"
            :disabled="!hasAcceptedEdsLimitation"
          >
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="bg-[#F5F5F5] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
                data-testid="eds-score-tile"
              >
                <span class="text-caption text-[#206683]">
                  {{ t('EDS Score') }}
                  <button v-if="hasAcceptedEdsLimitation" @click="emit('open-eds-sidebar')">
                    <VIcon
                      icon="info"
                      color="primary-600"
                      class="material-icons-outlined ml-1"
                      size="18"
                    />
                  </button>
                </span>
                <span
                  data-testid="eds-score-value"
                  class="text-subtitle-2 font-bold text-[#17485D]"
                >
                  <VBtn
                    v-if="!hasAcceptedEdsLimitation"
                    data-testid="open-eds-sidebar-btn"
                    class="px-1.5"
                    size="small"
                    height="22"
                    @click="emit('open-eds-sidebar')"
                  >
                    <VIcon icon="lock" class="material-icons-outlined" size="14" />
                    {{ t('Show') }}
                  </VBtn>
                  <template v-else>
                    {{ formatValue(edsScore?.scoreValue, NumberFormat.INTEGER) }}
                  </template>
                </span>
              </div>
            </template>
          </VTooltip>

          <div
            v-for="organSupport in organSupportList"
            :key="organSupport"
            class="bg-[#F5F5F5] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
            data-testid="organ-support-tile"
          >
            <p class="text-caption text-[#206683]">{{ organSupport }}</p>
            <div class="flex gap-1 align-center">
              <span class="organ-support-dot" />
              <span class="text-caption font-bold">{{ t('Active') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VCard>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import type {
  CaseDTO,
  ClinicalScoreResultDto,
  OrganSupportDTO,
  VitalSignItemDTO
} from '@/generated'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import { useI18n } from 'vue-i18n'
import PatientDetails from '@/components/patient/components/atoms/PatientDetails.vue'
import { usePatientVitals } from '../../composables/usePatientVitals'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import { parseOrganSupportDTO } from '@/util/parseOrganSupportDTO'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { NumberFormat } from '@/i18n/i18n'
import { useFormatValue } from '@/composables/useFormatValue'
import { usePluginsStore } from '@/stores/plugins'

interface Emits {
  (e: 'emergency-click', caseId: string): void
  (e: 'open-eds-sidebar'): void
}

const props = defineProps<{
  caze: CaseDTO
  vitalSigns: VitalSignItemDTO[]
  organSupport: OrganSupportDTO
  isSidebarVisible: boolean
  edsScore?: ClinicalScoreResultDto
}>()

const { t } = useI18n()
const { getLabelWithDefaultUnit } = useParameterLabel()
const userStore = useUserStore()
const { canCreateConsultations } = storeToRefs(userStore)
const { isConsultationDisabledForPatient } = userStore
const { isConnectEnabled, isEdsEnabled } = storeToRefs(usePluginsStore())
const { getFormattedPatientName } = useFormattedPatientDetails()
const noticeStore = useNoticeStore()
const router = useRouter()
const { hasAcceptedEdsLimitation, getEdsScoreTooltipText } = useEdsPlugin()
const { formatValue } = useFormatValue()

const edsTooltipText = computed(() => getEdsScoreTooltipText(props.edsScore))

const isConsultationDisabled = computed(() => {
  return isConsultationDisabledForPatient(props.caze.hospital?.id)
})

const reactiveVitalSigns = toRef(props.vitalSigns)
const { INV_BP, NON_INV_BP, HEART_RATE, SPO2, RR, TEMP } = usePatientVitals(reactiveVitalSigns)

const emit = defineEmits<Emits>()

interface VitalSignCardData {
  label: string
  value: string
  displayDate: string
}

const vitalSignsCards = computed<VitalSignCardData[]>(() => [
  {
    label: getLabelWithDefaultUnit(
      INV_BP.value.hasMeasurement
        ? Parameter.InvasiveBloodPressure
        : Parameter.NonInvasiveBloodPressure,
      true
    ),
    value: INV_BP.value.hasMeasurement
      ? formatValue(INV_BP.value.displayValue)
      : formatValue(NON_INV_BP.value.displayValue),
    displayDate: INV_BP.value.hasMeasurement
      ? INV_BP.value.displayDate
      : NON_INV_BP.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.HeartRate, true),
    value: formatValue(HEART_RATE.value.displayValue, NumberFormat.INTEGER),
    displayDate: HEART_RATE.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.OxygenSaturationSPO2, true),
    value: formatValue(SPO2.value.displayValue, NumberFormat.INTEGER),
    displayDate: SPO2.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.RespiratoryRate, true),
    value: formatValue(RR.value.displayValue, NumberFormat.INTEGER),
    displayDate: RR.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.Temperature, true),
    value: formatValue(TEMP.value.displayValue),
    displayDate: TEMP.value.displayDate
  }
])

const organSupportList = computed<string[]>(() => {
  return parseOrganSupportDTO(props.organSupport, t)
})

async function onCreateConsultation() {
  try {
    await router.push({
      name: routeNames.NEW_CONSULTATION,
      query: { caseId: props.caze.id }
    })
  } catch (error) {
    console.error((error as Error).message)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: 'Failed to navigate to consultation request'
    })
  }
}

async function redirectToPatientDetails() {
  await router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: props.caze.id }
  })
}
</script>

<style scoped lang="scss">
.organ-support-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #17b26a;
}

.v-tooltip > ::v-deep(.v-overlay__content) {
  background: black;
}
</style>
